<template>
  <div class="withdraw-page">
    <!-- ===== 顶部标题 ===== -->
    <div class="withdraw-header">
      <h1 class="withdraw-title">{{ $t("withdraw.title") }}</h1>
      <p class="withdraw-desc">{{ $t("withdraw.desc") }}</p>
    </div>

    <!-- ===== 主体 ===== -->
    <div class="withdraw-body">
      <div class="withdraw-container">
        <!-- ===== 未开启两步验证警告 ===== -->
        <div class="twofa-warning" v-if="isCheck">
          <span class="warning-icon">⚠️</span>
          <span class="warning-text">
            {{ $t("withdraw.twoFactor") }}
          </span>
          <button class="warning-btn" @click="goToSecurity">
            {{ $t("changePwd.goEnable") }}
          </button>
        </div>

        <!-- 余额 -->
        <div class="balance-card">
          <div>
            <span class="balance-label">{{ $t("withdraw.balance") }}</span>
            <span class="balance-amount">${{ wallet.balance }}</span>
          </div>
          <!-- <span class="balance-sub">≈ 0.42 BTC</span> -->
        </div>

        <!-- 提现方式切换 -->
        <div class="method-tabs">
          <button
            class="method-tab"
            :class="{ active: withdrawMethod === 'crypto' }"
            @click="withdrawMethod = 'crypto'"
          >
            {{ $t("withdraw.methodCrypto") }}
          </button>
          <!-- <button
            class="method-tab"
            :class="{ active: withdrawMethod === 'bank' }"
            @click="withdrawMethod = 'bank'"
          >
            🏦 银行提现
          </button> -->
        </div>

        <!-- ===== 加密货币提现 ===== -->
        <template v-if="withdrawMethod === 'crypto'">
          <!-- 币种 -->
          <div class="form-group">
            <label class="form-label">{{ $t("withdraw.selectCoin") }}</label>
            <div class="coin-grid">
              <div
                class="coin-option"
                v-for="coin in coins"
                :key="coin.value"
                :class="{ active: selectedCoin === coin.value }"
                @click="selectedCoin = coin.value"
              >
                <span class="coin-icon">
                  <img :src="getCoinIcon(coin.text)" alt="" class="coin-img" />
                </span>
                <span class="coin-name">{{ coin.text }}</span>
                <span class="coin-balance">{{
                  getCoinBalance(coin.value)
                }}</span>
              </div>
            </div>
          </div>

          <!-- 协议 -->
          <div class="form-group" v-if="selectedCoin">
            <label class="form-label">{{ $t("withdraw.selectNetwork") }}</label>
            <div class="protocol-grid">
              <div
                class="protocol-option"
                v-for="protocol in filteredProtocols"
                :key="protocol.id"
                :class="{ active: selectedProtocol === protocol.id }"
                @click="selectedProtocol = protocol.id"
              >
                <span class="protocol-name">{{ protocol.network }}</span>
                <span class="protocol-sub">{{ protocol.name }}</span>
              </div>
            </div>
          </div>

          <!-- 提现表单 -->
          <div class="withdraw-form" v-if="selectedProtocol">
            <!-- 提现地址 - 下拉选择 + 快捷绑定 -->
            <div class="form-group">
              <div class="address-label-row">
                <label class="form-label">{{ $t("withdraw.address") }}</label>
                <button class="btn-bind-address" @click="goToBindWallet">
                  {{ $t("withdraw.bindAddress") }}
                </button>
              </div>
              <div class="address-select-wrapper">
                <div
                  class="custom-select"
                  :class="{ open: addressDropdownOpen }"
                  @click="toggleAddressDropdown"
                >
                  <div class="custom-select__trigger">
                    <span
                      class="custom-select__value"
                      :class="{ placeholder: !selectedAddressId }"
                    >
                      {{
                        selectedAddressId
                          ? selectedAddress.text
                          : $t("withdraw.selectAddress")
                      }}
                    </span>
                    <span class="select-arrow">▼</span>
                  </div>
                  <div
                    class="custom-select__dropdown"
                    v-if="addressDropdownOpen"
                  >
                    <div
                      class="custom-select__option"
                      v-for="addr in filteredAddresses"
                      :key="addr.value"
                      :class="{ selected: selectedAddressId === addr.value }"
                      @click.stop="selectAddress(addr)"
                    >
                      {{ addr.text }}
                    </div>
                    <div
                      v-if="!filteredAddresses.length"
                      class="custom-select__empty"
                    >
                      {{ $t("withdraw.noAddress") }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="!filteredAddresses.length" class="empty-address-tip">
                {{ $t("withdraw.noAddressTip", { coin: selectedCoin }) }}
                <span class="tip-link" @click="goToBindWallet">{{
                  $t("withdraw.bindLink")
                }}</span>
              </div>
            </div>

            <!-- 提现金额 -->
            <div class="form-group">
              <label class="form-label">{{ $t("withdraw.amount") }}</label>
              <div class="amount-input-wrapper">
                <input
                  type="number"
                  class="amount-input"
                  v-model="withdrawAmount"
                  placeholder="0.00"
                />
                <span class="amount-currency">{{ selectedCoin }}</span>
              </div>
              <div class="amount-actions">
                <button class="btn-quick" @click="setQuickAmount(25)">
                  25%
                </button>
                <button class="btn-quick" @click="setQuickAmount(50)">
                  50%
                </button>
                <button class="btn-quick" @click="setQuickAmount(75)">
                  75%
                </button>
                <button class="btn-quick" @click="setQuickAmount(100)">
                  100%
                </button>
              </div>
            </div>

            <!-- 邮箱验证码 -->
            <div class="form-group">
              <label class="form-label">{{ $t("withdraw.emailCode") }}</label>
              <div class="captcha-row">
                <input
                  type="text"
                  class="form-input captcha-input"
                  v-model="emailCode"
                  :placeholder="$t('withdraw.emailCodePlaceholder')"
                  maxlength="6"
                />
                <button
                  class="btn-captcha"
                  @click="openTwoFactorModal"
                  :disabled="codeCountdown > 0"
                >
                  {{
                    codeCountdown > 0
                      ? $t("withdraw.resend", { count: codeCountdown })
                      : $t("withdraw.sendCode")
                  }}
                </button>
              </div>
            </div>

            <!-- 提现信息 -->
            <div class="withdraw-info">
              <div class="info-row">
                <span class="info-label">{{
                  $t("withdraw.expectedArrival")
                }}</span>
                <span class="info-value">{{ $t("withdraw.arrivalTime") }}</span>
              </div>

              <div class="info-row">
                <span class="info-label">{{ $t("withdraw.networkFee") }}</span>
                <span class="info-value"
                  >{{ networkFee }} {{ selectedCoinLabel }}</span
                >
              </div>
              <div class="info-row">
                <span class="info-label">{{
                  $t("withdraw.actualArrival")
                }}</span>
                <span class="info-value highlight-text">
                  {{ actualAmount }} {{ selectedCoinLabel }}
                </span>
              </div>
            </div>

            <button
              class="btn-withdraw"
              @click="handleWithdraw"
              :disabled="!isValid"
            >
              {{
                isWithdrawing
                  ? $t("withdraw.submitting")
                  : $t("withdraw.confirm")
              }}
            </button>
          </div>

          <div v-else class="empty-state">{{ $t("withdraw.selectFirst") }}</div>
        </template>

        <!-- ===== 银行提现 ===== -->
        <template v-if="withdrawMethod === 'bank'">
          <div class="bank-withdraw">
            <!-- 银行账户 -->
            <div class="form-group">
              <label class="form-label">{{ $t("withdraw.selectBank") }}</label>
              <select class="form-select" v-model="selectedBank">
                <option value="" disabled>
                  {{ $t("withdraw.selectBankPlaceholder") }}
                </option>
                <option value="1">
                  **** **** **** 8888 - {{ $t("withdraw.bankBOC") }}
                </option>
                <option value="2">
                  **** **** **** 6666 - {{ $t("withdraw.bankICBC") }}
                </option>
              </select>
              <button class="btn-add-bank">{{ $t("withdraw.addBank") }}</button>
            </div>

            <!-- 法币金额 -->
            <div class="form-group">
              <label class="form-label">{{ $t("withdraw.bankAmount") }}</label>
              <div class="amount-input-wrapper">
                <input
                  type="number"
                  class="amount-input"
                  v-model="bankAmount"
                  placeholder="0.00"
                />
                <span class="amount-currency">USD</span>
              </div>
            </div>

            <!-- 汇率信息 -->
            <div class="exchange-info">
              <div class="exchange-row">
                <span>{{ $t("withdraw.exchangeRate") }}</span>
                <span>1 USD = 7.25 CNY</span>
              </div>
              <div class="exchange-row">
                <span>{{ $t("withdraw.expectedArrival") }}</span>
                <span
                  >{{
                    bankAmount ? "¥" + (bankAmount * 7.25).toFixed(2) : "¥0.00"
                  }}
                  CNY</span
                >
              </div>
              <div class="exchange-row">
                <span>{{ $t("withdraw.arrivalTimeLabel") }}</span>
                <span>{{ $t("withdraw.bankArrivalTime") }}</span>
              </div>
            </div>

            <button
              class="btn-withdraw"
              @click="handleBankWithdraw"
              :disabled="!bankAmount"
            >
              {{ $t("withdraw.bankConfirm") }}
            </button>
          </div>
        </template>

        <!-- ========================================================== -->
        <!-- ===== 提现注意事项 ===== -->
        <!-- ========================================================== -->
        <div class="notice-section">
          <div class="notice-header">
            <span class="notice-title">{{ $t("withdraw.noticeTitle") }}</span>
          </div>
          <div class="notice-grid">
            <div class="notice-item" v-for="item in notices" :key="item.id">
              <span class="notice-icon">{{ item.icon }}</span>
              <div class="notice-content">
                <h4>{{ item.title }}</h4>
                <p>{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 两步验证弹窗（独立组件） ===== -->
    <TwoFactorModal
      ref="twoFactorModal"
      :desc="$t('withdraw.twoFactorDesc')"
      @confirm="onTwoFactorConfirm"
    />
  </div>
</template>

<script>
import TwoFactorModal from "@/components/TwoFactorModal.vue";
import { getUserInfo } from "@/utils/fun.js";

export default {
  name: "WithdrawPage",
  components: {
    TwoFactorModal,
  },
  data() {
    return {
      info: {},
      wallet: {},
      withdrawMethod: "crypto", // crypto | bank
      selectedCoin: null,
      selectedProtocol: null,
      selectedAddressId: "",
      addressDropdownOpen: false,
      withdrawAmount: "",
      isWithdrawing: false,
      selectedBank: "",
      bankAmount: "",

      // 邮箱验证码
      emailCode: "",
      isEmailCodeSent: false,
      codeCountdown: 0,
      codeTimer: null,

      // 币种列表（从接口获取）
      coins: [],

      notices: [
        {
          id: 1,
          icon: "⏱️",
          title: this.$t("withdraw.notice1Title"),
          desc: this.$t("withdraw.notice1Desc"),
        },
        {
          id: 2,
          icon: "💰",
          title: this.$t("withdraw.notice2Title"),
          desc: this.$t("withdraw.notice2Desc"),
        },
        {
          id: 3,
          icon: "🔗",
          title: this.$t("withdraw.notice3Title"),
          desc: this.$t("withdraw.notice3Desc"),
        },
        {
          id: 4,
          icon: "📊",
          title: this.$t("withdraw.notice4Title"),
          desc: this.$t("withdraw.notice4Desc"),
        },
      ],
    };
  },

  computed: {
    isCheck() {
      //未开启 并且 真实用户
      return !this.isOpen && !this.isMoni;
    },
    isOpen() {
      return this.info.is_two_factor == 2;
    },
    isMoni() {
      return this.info.is_moni == 2;
    },
    filteredProtocols() {
      const coin = this.coins.find((c) => c.value === this.selectedCoin);
      return coin ? coin.protocol || [] : [];
    },

    selectedProtocolObj() {
      return this.filteredProtocols.find((p) => p.id === this.selectedProtocol);
    },

    selectedProtocolLabel() {
      return this.selectedProtocolObj ? this.selectedProtocolObj.network : "";
    },

    selectedCoinLabel() {
      const c = this.coins.find((c) => c.value === this.selectedCoin);
      return c ? c.text : "";
    },

    networkFee() {
      return this.selectedProtocolObj
        ? this.getProtocolFee(this.selectedProtocolObj)
        : "0";
    },

    actualAmount() {
      const amount = parseFloat(this.withdrawAmount) || 0;
      const fee = this.networkFee || 0;
      const actual = amount - fee;
      return actual > 0 ? actual.toFixed(4) : "0.0000";
    },

    // ===== 🆕 过滤后的地址列表（从接口 address_list 获取） =====
    filteredAddresses() {
      if (!this.selectedProtocolObj) return [];
      return this.selectedProtocolObj.address_list || [];
    },

    // ===== 🆕 选中的地址对象 =====
    selectedAddress() {
      return this.filteredAddresses.find(
        (addr) => addr.value === this.selectedAddressId,
      );
    },

    isValid() {
      return (
        this.selectedAddressId &&
        this.selectedAddress &&
        this.withdrawAmount &&
        parseFloat(this.withdrawAmount) > 0
      );
    },
  },

  watch: {
    // ===== 🆕 切换币种或协议时，重置选中的地址 =====
    selectedCoin() {
      const protocols = this.filteredProtocols;
      this.selectedProtocol = protocols.length ? protocols[0].id : null;
      this.selectedAddressId = "";
    },

    selectedProtocol() {
      this.selectedAddressId = "";
    },
  },
  created() {
    this.getCurrencyList();
    this.getWallet();
    this.info = getUserInfo();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    if (this.codeTimer) {
      clearInterval(this.codeTimer);
    }
  },
  methods: {
    // ============================================================
    // 0. 去开启两步验证
    // ============================================================
    goToSecurity() {
      // 同步更新 URL 的 tab 参数
      this.$router
        .replace({ name: "Setting", query: { tab: "security" } })
        .catch(() => {});
    },
    // ============================================================
    // 手续费：min~max 随机 + 5 分钟缓存（key: 币种_网络）
    // ============================================================
    // 获取小数位数
    getDecimals(value) {
      const str = String(value);
      const idx = str.indexOf(".");
      return idx === -1 ? 0 : str.length - idx - 1;
    },

    // min ~ max 之间随机一个数，保留与 min/max 相同的小数精度
    randomFee(min, max) {
      const minNum = parseFloat(min) || 0;
      const maxNum = parseFloat(max) || 0;
      if (maxNum <= minNum) return minNum.toFixed(6);
      const decimals = Math.max(
        this.getDecimals(minNum),
        this.getDecimals(maxNum),
      );
      const random = minNum + Math.random() * (maxNum - minNum);
      return random.toFixed(decimals);
    },

    // 获取协议对应手续费（带缓存：5 分钟）
    getProtocolFee(protocol) {
      if (!protocol) return "0";
      const coinName = this.selectedCoinLabel; // 如 USDT
      const network = protocol.network; // 如 ERC20
      const key = `${coinName}_${network}`;
      const now = this.$dayjs().valueOf();
      const CACHE_TTL = 5 * 60 * 1000; // 5 分钟

      // 读取缓存
      const cached = localStorage.getItem(key);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (
            parsed &&
            parsed.fee !== undefined &&
            parsed.t &&
            now - parsed.t < CACHE_TTL
          ) {
            return parsed.fee;
          }
        } catch (e) {
          // 缓存解析失败，忽略并重新生成
        }
      }

      // 生成随机手续费并写入缓存
      const fee = this.randomFee(
        protocol.transfer_fee_min,
        protocol.transfer_fee_max,
      );
      localStorage.setItem(key, JSON.stringify({ fee, t: now }));
      return fee;
    },

    getWallet() {
      this.$api.userWallet.getWallet().then((res) => {
        this.wallet = res.data || {};
      });
    },
    getCurrencyList() {
      this.$api.chain.getCurrencyList().then((res) => {
        if (res.code === 0) {
          this.coins = res.data || [];
          if (this.coins.length) {
            this.selectedCoin = this.coins[0].value;
          }
        }
      });
    },

    getCoinIcon(text) {
      if (!text) return require("@/assets/coins/usdt.png");
      const map = {
        BTC: require("@/assets/coins/btc.png"),
        ETH: require("@/assets/coins/eth.png"),
        TRX: require("@/assets/coins/trx.png"),
        USDT: require("@/assets/coins/usdt.png"),
        USDC: require("@/assets/coins/usdc.png"),
      };
      return map[text.toUpperCase()] || require("@/assets/coins/usdt.png");
    },
    getCoinBalance(value) {
      const coin = this.coins.find((c) => c.value === value);
      if (!coin || !coin.protocol || !coin.protocol.length) return "0";
      const wallet = coin.protocol[0].wallet;
      return wallet ? this.formatBalance(wallet.number) : "0";
    },
    formatBalance(num) {
      if (num === null || num === undefined) return "0";
      const n = parseFloat(num);
      if (isNaN(n)) return "0";
      // 大数格式化
      if (n >= 1000000) return (n / 1000000).toFixed(2) + "M";
      if (n >= 1000) return (n / 1000).toFixed(2) + "K";
      return n.toFixed(4);
    },

    setQuickAmount(percent) {
      const coin = this.coins.find((c) => c.value === this.selectedCoin);
      if (coin && coin.protocol && coin.protocol.length) {
        const wallet = coin.protocol[0].wallet;
        const balance = wallet ? parseFloat(wallet.number) || 0 : 0;
        this.withdrawAmount = (balance * (percent / 100)).toFixed(4);
      }
    },

    // ===== 🆕 自定义下拉框：展开/收起 =====
    toggleAddressDropdown() {
      this.addressDropdownOpen = !this.addressDropdownOpen;
    },

    // ===== 🆕 自定义下拉框：选择地址 =====
    selectAddress(addr) {
      this.selectedAddressId = addr.value;
      this.addressDropdownOpen = false;
    },

    // ===== 🆕 自定义下拉框：点击外部关闭 =====
    handleClickOutside(e) {
      const el = this.$el.querySelector(".custom-select");
      if (el && !el.contains(e.target)) {
        this.addressDropdownOpen = false;
      }
    },

    // ===== 🆕 跳转到绑定地址页面 =====
    goToBindWallet() {
      console.log("🔗 跳转到绑定地址页面");
      // 跳转到绑定钱包页面
      // this.$router.push({ path: '/bind-wallet' });
      //   alert("🔗 跳转到绑定地址页面\n\n请先完成地址绑定");
      this.$router.push({ path: "/bind-address" });
    },

    // ============================================================
    // 两步验证：点击「发送验证码」→ 打开两步验证弹窗
    // ============================================================
    openTwoFactorModal() {
      // 校验提现地址是否已选择
      if (!this.selectedAddressId) {
        this.$message.warning(this.$t("withdraw.selectAddressFirst"));
        return;
      }

      //模拟用户跳过两步验证
      if (this.info.is_moni == 2) {
        // 调用发送邮箱验证码接口
        this.$api.code
          .sendSafetyCode({
            scene: "withdraw",
            twoCode: "",
          })
          .then((res) => {
            if (res.code == 1) {
              // 验证失败，显示错误并重置
              this.$message.warning(
                res.msg || this.$t("bindAddress.msgCodeError"),
              );
              return;
            }
            this.$message.success(res.msg);
          });
        return;
      }

      // 打开两步验证弹窗（组件内部会重置状态并聚焦）
      this.$refs.twoFactorModal.open();
    },

    // ============================================================
    // 两步验证通过 → 发送邮箱验证码
    // ============================================================
    onTwoFactorConfirm(twoCode) {
      // 设置按钮为验证中状态
      this.$refs.twoFactorModal.setVerifying(true);

      // 调用发送邮箱验证码接口
      this.$api.code
        .sendSafetyCode({
          scene: "withdraw",
          twoCode,
        })
        .then((res) => {
          if (res.code == 1) {
            // 验证失败，显示错误并重置
            this.$refs.twoFactorModal.setError(
              res.msg || this.$t("withdraw.codeError"),
            );
            return;
          }
          this.$message.success(res.msg);
          this.$refs.twoFactorModal.close();
          this.sendEmailCode();
        });
    },

    // ============================================================
    // 发送邮箱验证码接口
    // ============================================================
    async sendEmailCode() {
      if (this.codeCountdown > 0) return;

      // 模拟调用发送邮箱验证码接口
      console.log("📧 调用发送邮箱验证码接口");
      console.log("   → 邮箱: user@example.com");
      console.log("   → 币种:", this.selectedCoin);
      console.log("   → 协议:", this.selectedProtocol);

      // 实际场景中，这里会调用后端 API
      // const res = await this.$api.code.sendSafetyCode({
      //   scene: "withdraw",
      // });

      // if (res.code == 1) {
      //   this.$message.warning(res.msg);
      //   return false;
      // }

      // this.$message.success(res.msg);

      // 模拟发送成功
      this.isEmailCodeSent = true;

      // 开始倒计时
      this.codeCountdown = 60;
      this.codeTimer = setInterval(() => {
        this.codeCountdown--;
        if (this.codeCountdown <= 0) {
          clearInterval(this.codeTimer);
          this.codeTimer = null;
        }
      }, 1000);

      return true;
    },

    async handleWithdraw() {
      if (!this.isValid) return;

      //判断地址是否选择
      if (!this.selectedAddressId) {
        this.$message.warning(this.$t("withdraw.selectAddressFirst"));
        return;
      }

      //判断手续费是否足够
      if (Number(this.networkFee) >= Number(this.withdrawAmount)) {
        this.$message.warning(this.$t("withdraw.insufficientHandlingFee"));
        return;
      }

      // ===== 打开两步验证弹窗 =====
      // 实际场景中，这里会弹出两步验证弹窗
      // 此处简化，直接弹出确认框

      // if (
      //   !confirm(
      //     `确认提现 ${this.withdrawAmount} ${this.selectedCoin} 到\n${this.selectedAddress.value}？`,
      //   )
      // ) {
      //   return;
      // }

      this.isWithdrawing = true;
      console.log("提现请求:", {
        coin: this.selectedCoin,
        protocol: this.selectedProtocol,
        addressId: this.selectedAddressId,
        address: this.selectedAddress.value,
        label: this.selectedAddress.text,
        amount: this.withdrawAmount,
        fee: Number(this.networkFee),
        actual: this.actualAmount,
      });

      const res = await this.$api.userWallet.withdraw({
        c_id: this.selectedCoin,
        number: Number(this.withdrawAmount),
        fee: Number(this.networkFee),
        code: this.emailCode,
        address: this.selectedAddress.value,
      });
      this.isWithdrawing = false;
      if (res.code === 1) {
        this.$message.warning(res.msg);
        return;
      }

      this.$message.success(res.msg);

      setTimeout(() => {
        this.$router.push({ name: "Member", query: { tab: "withdraws" } });
      }, 1500);
    },

    handleBankWithdraw() {
      console.log("银行提现:", {
        bank: this.selectedBank,
        amount: this.bankAmount,
        cny: this.bankAmount * 7.25,
      });
      alert(this.$t("withdraw.bankSuccess"));
    },
  },
};
</script>

<style scoped>
/* ============================================================
   提现页面
   ============================================================ */
.withdraw-page {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial,
    sans-serif;
  background: #0a0e1a;
  color: #fff;
  min-height: 100vh;
  padding: 24px 20px;
}

/* ===== 顶部 ===== */
.withdraw-header {
  text-align: center;
  padding: 16px 0 20px;
}

.withdraw-title {
  font-size: 30px;
  font-weight: 800;
  margin: 0;
}

.withdraw-title .highlight {
  background: linear-gradient(135deg, #f7971e, #ffd200);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.withdraw-desc {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
  margin: 4px 0 0;
}

/* ===== 容器 ===== */
.withdraw-container {
  max-width: 900px;
  margin: 0 auto;
}

/* ===== 余额 ===== */
.balance-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(255, 215, 0, 0.04);
  border: 1px solid rgba(255, 215, 0, 0.06);
  border-radius: 12px;
  margin-bottom: 18px;
}

.balance-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  display: block;
}

.balance-amount {
  font-size: 26px;
  font-weight: 700;
}

.balance-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.25);
}

/* ===== Tabs ===== */
.method-tabs {
  display: flex;
  gap: 0;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 18px;
}

.method-tab {
  flex: 1;
  padding: 11px 16px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.method-tab:hover {
  color: rgba(255, 255, 255, 0.6);
}

.method-tab.active {
  color: #fff;
  background: rgba(255, 215, 0, 0.06);
  box-shadow: inset 0 -2px 0 #ffd700;
}

/* ===== 表单 ===== */
.form-group {
  margin-bottom: 14px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 8px;
}

/* ===== 🆕 地址标签行 ===== */
.address-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.address-label-row .form-label {
  margin-bottom: 0;
}

.btn-bind-address {
  padding: 4px 14px;
  border-radius: 6px;
  background: rgba(0, 180, 255, 0.06);
  border: 1px solid rgba(0, 180, 255, 0.08);
  color: #00b4ff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
  white-space: nowrap;
}

.btn-bind-address:hover {
  background: rgba(0, 180, 255, 0.12);
  border-color: rgba(0, 180, 255, 0.15);
}

/* ===== 🆕 地址下拉框 ===== */
.address-select-wrapper {
  position: relative;
}

.form-select {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.25s ease;
  appearance: none;
  cursor: pointer;
}

.form-select:focus {
  border-color: #ffd700;
}

.form-select option {
  background: #1a1f35;
  color: #fff;
  padding: 8px;
}

.select-arrow {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.2);
  pointer-events: none;
}

/* ===== 🆕 自定义下拉框 ===== */
.custom-select {
  position: relative;
  width: 100%;
}

.custom-select__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 40px 12px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  cursor: pointer;
  transition: border-color 0.25s ease, background 0.25s ease;
  user-select: none;
}

.custom-select__trigger:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

.custom-select.open .custom-select__trigger {
  border-color: #ffd700;
  background: rgba(255, 215, 0, 0.04);
}

.custom-select__value {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-select__value.placeholder {
  color: rgba(255, 255, 255, 0.25);
}

.custom-select .select-arrow {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.2);
  pointer-events: none;
  transition: transform 0.25s ease;
}

.custom-select.open .select-arrow {
  transform: translateY(-50%) rotate(180deg);
  color: #ffd700;
}

.custom-select__dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  background: #1a1f35;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  padding: 6px;
}

.custom-select__option {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-select__option:hover {
  background: rgba(255, 215, 0, 0.08);
  color: #fff;
}

.custom-select__option.selected {
  color: #ffd700;
  font-weight: 600;
  background: rgba(255, 215, 0, 0.06);
}

.custom-select__empty {
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.25);
}

/* 自定义滚动条 */
.custom-select__dropdown::-webkit-scrollbar {
  width: 6px;
}

.custom-select__dropdown::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 3px;
}

.custom-select__dropdown::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

.custom-select__dropdown::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* ===== 🆕 空地址提示 ===== */
.empty-address-tip {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
  padding: 8px 0 4px 0;
}

.tip-link {
  color: #ffd700;
  cursor: pointer;
  text-decoration: underline;
}

.tip-link:hover {
  color: #ffed4a;
}

/* ===== 币种 ===== */
.coin-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.coin-option {
  padding: 12px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 2px solid rgba(255, 255, 255, 0.04);
  text-align: center;
  cursor: pointer;
  transition: all 0.25s ease;
}

.coin-option:hover {
  border-color: rgba(255, 255, 255, 0.08);
}

.coin-option.active {
  border-color: #ffd700;
  background: rgba(255, 215, 0, 0.06);
}

.coin-icon {
  display: block;
  font-size: 22px;
}

.coin-img {
  width: 28px;
  height: 28px;
  object-fit: contain;
  display: inline-block;
  vertical-align: middle;
}

.coin-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.coin-balance {
  display: block;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.2);
  margin-top: 2px;
}

.coin-option.active .coin-name {
  color: #fff;
}

.coin-option.active .coin-balance {
  color: rgba(255, 255, 255, 0.4);
}

/* ===== 协议 ===== */
.protocol-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.protocol-option {
  padding: 10px 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  border: 2px solid rgba(255, 255, 255, 0.04);
  text-align: center;
  cursor: pointer;
  transition: all 0.25s ease;
}

.protocol-option:hover {
  border-color: rgba(255, 255, 255, 0.08);
}

.protocol-option.active {
  border-color: #ffd700;
  background: rgba(255, 215, 0, 0.06);
}

.protocol-name {
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
}

.protocol-option.active .protocol-name {
  color: #fff;
}

.protocol-sub {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.2);
}

/* ===== 表单输入 ===== */
.form-input {
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.25s ease;
}

.form-input:focus {
  border-color: #ffd700;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.15);
}

/* ===== 金额输入 ===== */
.amount-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.25s ease;
}

.amount-input-wrapper:focus-within {
  border-color: #ffd700;
}

.amount-input {
  flex: 1;
  padding: 12px 16px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  font-family: inherit;
  outline: none;
}

.amount-input::placeholder {
  color: rgba(255, 255, 255, 0.15);
  font-weight: 400;
}

.amount-currency {
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.3);
}

/* ===== 快捷按钮 ===== */
.amount-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.btn-quick {
  flex: 1;
  padding: 6px 12px;
  border-radius: 6px;
  background: rgba(255, 215, 0, 0.04);
  border: 1px solid rgba(255, 215, 0, 0.06);
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
}

.btn-quick:hover {
  background: rgba(255, 215, 0, 0.08);
  border-color: rgba(255, 215, 0, 0.12);
  color: #fff;
}

/* ===== 提现信息 ===== */
.withdraw-info {
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
}

.info-label {
  color: rgba(255, 255, 255, 0.3);
}

.info-value {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

.info-value.highlight-text {
  color: #ffd700;
  font-weight: 700;
}

/* ===== 提现按钮 ===== */
.btn-withdraw {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f7971e, #ffd200);
  color: #0a0e1a;
  font-size: 16px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
}

.btn-withdraw:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(255, 215, 0, 0.25);
}

.btn-withdraw:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ===== 银行提现 ===== */
.btn-add-bank {
  margin-top: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  background: rgba(255, 215, 0, 0.04);
  border: 1px dashed rgba(255, 215, 0, 0.15);
  color: rgba(255, 215, 0, 0.4);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
}

.btn-add-bank:hover {
  background: rgba(255, 215, 0, 0.06);
  border-color: rgba(255, 215, 0, 0.25);
  color: #ffd700;
}

.exchange-info {
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 10px;
  margin-bottom: 16px;
}

.exchange-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
}

.exchange-row span:last-child {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

/* ===== 空状态 ===== */
.empty-state {
  text-align: center;
  padding: 32px 16px;
  color: rgba(255, 255, 255, 0.2);
  font-size: 14px;
}

/* ===== 邮箱验证码 ===== */
.captcha-row {
  display: flex;
  gap: 10px;
}

.captcha-input {
  flex: 1;
}

.btn-captcha {
  padding: 12px 20px;
  border-radius: 10px;
  background: rgba(255, 215, 0, 0.06);
  border: 1px solid rgba(255, 215, 0, 0.06);
  color: #ffd700;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: inherit;
  white-space: nowrap;
}

.btn-captcha:hover:not(:disabled) {
  background: rgba(255, 215, 0, 0.12);
}

.btn-captcha:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ============================================================
   注意事项
   ============================================================ */
.notice-section {
  margin-top: 24px;
  padding: 18px 22px 22px;
  background: rgba(255, 215, 0, 0.02);
  border: 1px solid rgba(255, 215, 0, 0.06);
  border-radius: 12px;
}

.notice-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.notice-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
}

.notice-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
}

.notice-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.01);
  border-radius: 8px;
  transition: background 0.2s;
}

.notice-item:hover {
  background: rgba(255, 255, 255, 0.02);
}

.notice-icon {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.notice-content h4 {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 2px 0;
}

.notice-content p {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.25);
  margin: 0;
  line-height: 1.5;
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 768px) {
  .withdraw-page {
    padding: 16px 12px;
  }

  .withdraw-title {
    font-size: 24px;
  }

  .balance-amount {
    font-size: 22px;
  }

  .coin-grid,
  .protocol-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .amount-actions {
    flex-wrap: wrap;
  }

  .btn-quick {
    flex: 1;
    min-width: 40px;
  }

  .notice-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .notice-section {
    padding: 14px 16px 18px;
  }

  .notice-item {
    padding: 8px 10px;
  }

  .address-label-row {
    flex-wrap: wrap;
    gap: 6px;
  }

  .form-select {
    font-size: 13px;
    padding: 11px 36px 11px 14px;
  }
}

@media (max-width: 400px) {
  .balance-card {
    flex-direction: column;
    text-align: center;
    gap: 4px;
  }

  .coin-grid,
  .protocol-grid {
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }

  .coin-option,
  .protocol-option {
    padding: 8px 6px;
  }

  .amount-input {
    font-size: 16px;
  }

  .withdraw-info {
    padding: 10px 14px;
  }

  .info-row {
    font-size: 13px;
  }

  .notice-section {
    padding: 12px;
  }

  .notice-content p {
    font-size: 12px;
  }

  .btn-bind-address {
    font-size: 11px;
    padding: 3px 10px;
  }

  .form-select {
    font-size: 12px;
    padding: 10px 32px 10px 12px;
  }
}

/* ===== 未开启两步验证警告 ===== */
.twofa-warning {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: rgba(255, 77, 79, 0.08);
  border: 1px solid rgba(255, 77, 79, 0.2);
  border-radius: 10px;
  margin-bottom: 20px;
}

.warning-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.warning-text {
  flex: 1;
  font-size: 14px;
  color: rgba(255, 107, 107, 0.9);
  line-height: 1.5;
}

.warning-btn {
  padding: 6px 20px;
  border-radius: 8px;
  background: rgba(255, 77, 79, 0.15);
  border: 1px solid rgba(255, 77, 79, 0.3);
  color: #ff6b6b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  white-space: nowrap;
}

.warning-btn:hover {
  background: rgba(255, 77, 79, 0.25);
  color: #fff;
}
</style>
